import { useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import type {
  ComponentPageData,
  RawQuestionSettings,
} from '@/features/content/useComponent';

import { Tooltip } from '@/components/Tooltip/Tooltip';
import { StudentViewErrorDialog } from '@/features/studentView/StudentViewErrorDialog';
import { CHAPTER_AI_SETTINGS_TARGET_ID } from '@/features/productTours/tourDefinitions';
import { useTourTarget } from '@/features/productTours/useTourTarget';
import { useOpenStudentView } from '@/features/studentView/useOpenStudentView';

import { AssignPreLectureDialog } from './AssignPreLectureDialog';
import { AssignQuestionsDialog } from './AssignQuestionsDialog';
import { ManageDueDateDialog } from './ManageDueDateDialog';
import { AISettingsDialog } from './AISettingsDialog';
import { QuestionSettingsDialog } from './QuestionSettingsDialog';
import { VisibilitySettingsDialog } from './VisibilitySettingsDialog';
import { useControlPanelStatus } from './hooks/useControlPanelStatus';
import styles from './ControlPanelSummary.module.css';

interface ControlPanelSummaryProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  // Course-level defaults, so the dialogs can name the value each
  // "Use course default" option actually resolves to.
  coursePrelectureQuestionSettings?: RawQuestionSettings | null;
  courseQuestionSettings?: RawQuestionSettings | null;
}

type DialogId =
  | 'aiSettings'
  | 'assignHomework'
  | 'assignPreLecture'
  | 'homeworkDueDate'
  | 'homeworkSettings'
  | 'preLectureDueDate'
  | 'preLectureSettings'
  | 'visibility'
  | null;

const MOBILE_MEDIA_QUERY = '(max-width: 47.99rem)';

function isMobileViewport() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_MEDIA_QUERY).matches
    : false;
}

function StatusRow({
  actions,
  title,
  text,
}: {
  actions: ReactNode;
  text: string;
  title: string;
}) {
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <p className={styles.rowTitle}>{title}</p>
        <p className={styles.rowBody}>{text}</p>
      </div>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
}

export function ControlPanelSummary({
  component,
  componentId,
  courseId,
  coursePrelectureQuestionSettings,
  courseQuestionSettings,
}: ControlPanelSummaryProps) {
  const status = useControlPanelStatus(component);
  const aiSettingsTargetRef = useTourTarget(CHAPTER_AI_SETTINGS_TARGET_ID);
  const openStudentView = useOpenStudentView();
  const [isMobileViewportActive, setIsMobileViewportActive] = useState(
    isMobileViewport,
  );
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState<DialogId>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobileViewportActive(event.matches);
    };

    setIsMobileViewportActive(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => {
        mediaQueryList.removeEventListener('change', handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);
    return () => {
      mediaQueryList.removeListener(handleChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewportActive) {
      setIsMobilePanelOpen(false);
    }
  }, [isMobileViewportActive]);

  const openDialogFromPanel = (dialogId: Exclude<DialogId, null>) => {
    setIsMobilePanelOpen(false);
    setOpenDialog(dialogId);
  };

  const statusRows = (
    <div className={styles.rows}>
      <StatusRow
        actions={
          <>
            <Tooltip content="Open this page in a new tab exactly as a student sees it. Interactions are saved to a test account, never a real student.">
              <Button
                className={styles.actionButton}
                disabled={openStudentView.isPending}
                onClick={() => {
                  setIsMobilePanelOpen(false);
                  openStudentView.mutate({
                    contentId: componentId,
                    courseId,
                  });
                }}
                size="small"
                variant="ghost"
              >
                VIEW AS STUDENT
              </Button>
            </Tooltip>
            <Button
              className={styles.actionButton}
              onClick={() => openDialogFromPanel('visibility')}
              size="small"
              variant="ghost"
            >
              VISIBILITY SETTINGS
            </Button>
          </>
        }
        text={status.visibility.text}
        title="Visibility"
      />

      {status.homework.isVisible ? (
        <StatusRow
          actions={
            <>
              {status.homework.showAssignAction ? (
                <Button
                  className={styles.actionButton}
                  onClick={() => openDialogFromPanel('assignHomework')}
                  size="small"
                  variant="ghost"
                >
                  ASSIGN QUESTIONS
                </Button>
              ) : null}
              <Button
                className={styles.actionButton}
                onClick={() => openDialogFromPanel('homeworkDueDate')}
                size="small"
                variant="ghost"
              >
                MANAGE DUE DATE
              </Button>
              <Button
                className={styles.actionButton}
                onClick={() => openDialogFromPanel('homeworkSettings')}
                size="small"
                variant="ghost"
              >
                QUESTION SETTINGS
              </Button>
            </>
          }
          text={status.homework.text}
          title="Homework"
        />
      ) : null}

      {status.preLecture.isVisible ? (
        <StatusRow
          actions={
            <>
              {status.preLecture.showAssignAction ? (
                <Button
                  className={styles.actionButton}
                  onClick={() => openDialogFromPanel('assignPreLecture')}
                  size="small"
                  variant="ghost"
                >
                  ASSIGN PRE-LECTURE
                </Button>
              ) : component.controlPanel.hasPreLectureQuestionSection ? (
                <>
                  <Button
                    className={styles.actionButton}
                    onClick={() => openDialogFromPanel('preLectureDueDate')}
                    size="small"
                    variant="ghost"
                  >
                    PRE-LECTURE DUE DATE
                  </Button>
                  <Button
                    className={styles.actionButton}
                    onClick={() => openDialogFromPanel('preLectureSettings')}
                    size="small"
                    variant="ghost"
                  >
                    PRE-LECTURE SETTINGS
                  </Button>
                </>
              ) : null}
            </>
          }
          text={status.preLecture.text}
          title="Pre-lecture"
        />
      ) : null}

      <StatusRow
        actions={
          <Button
            className={styles.actionButton}
            onClick={() => openDialogFromPanel('aiSettings')}
            ref={aiSettingsTargetRef}
            size="small"
            variant="ghost"
          >
            AI SETTINGS
          </Button>
        }
        text="AI hints, practice problems, and the Kai assistant for this chapter."
        title="AI features"
      />
    </div>
  );

  const mobileSummaryText =
    status.homework.isVisible || status.preLecture.isVisible
      ? 'Manage visibility, assignments, due dates, and question settings.'
      : status.visibility.text;

  return (
    <>
      <Card
        aria-labelledby="instructor-control-panel-heading"
        as="section"
        className={styles.panel}
        data-testid="instructor-control-panel"
        variant="standard"
      >
        <div className={styles.header}>
          <p className={styles.eyebrow}>Instructor Controls</p>
          <h2 className={styles.title} id="instructor-control-panel-heading">
            Assignment settings
          </h2>
        </div>

        {isMobileViewportActive ? (
          <div className={styles.mobileSummary}>
            <p className={styles.mobileSummaryText}>{mobileSummaryText}</p>
            <Button
              aria-controls="instructor-control-panel-sheet"
              aria-expanded={isMobilePanelOpen}
              className={styles.mobileSettingsButton}
              onClick={() => setIsMobilePanelOpen(true)}
              size="small"
              variant="secondary"
            >
              Settings
            </Button>
          </div>
        ) : (
          statusRows
        )}
      </Card>

      {isMobileViewportActive && isMobilePanelOpen ? (
        <>
          <button
            aria-hidden="true"
            className={styles.mobileBackdrop}
            onClick={() => setIsMobilePanelOpen(false)}
            tabIndex={-1}
            type="button"
          />
          <Card
            aria-labelledby="instructor-control-panel-sheet-heading"
            as="section"
            className={styles.mobileSheet}
            id="instructor-control-panel-sheet"
            variant="elevated"
          >
            <div className={styles.mobileSheetHeader}>
              <div>
                <p className={styles.eyebrow}>Instructor Controls</p>
                <h2
                  className={styles.mobileSheetTitle}
                  id="instructor-control-panel-sheet-heading"
                >
                  Assignment settings
                </h2>
              </div>
              <Button
                className={styles.actionButton}
                onClick={() => setIsMobilePanelOpen(false)}
                size="small"
                variant="ghost"
              >
                Done
              </Button>
            </div>

            {statusRows}
          </Card>
        </>
      ) : null}

      <VisibilitySettingsDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        onOpenChange={(nextOpen) => setOpenDialog(nextOpen ? 'visibility' : null)}
        open={openDialog === 'visibility'}
      />
      <AssignQuestionsDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'assignHomework' : null)
        }
        open={openDialog === 'assignHomework'}
      />
      <ManageDueDateDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        mode="homework"
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'homeworkDueDate' : null)
        }
        open={openDialog === 'homeworkDueDate'}
      />
      <ManageDueDateDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        mode="preLecture"
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'preLectureDueDate' : null)
        }
        open={openDialog === 'preLectureDueDate'}
      />
      <AISettingsDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        courseQuestionSettings={courseQuestionSettings}
        onOpenChange={(next) => setOpenDialog(next ? 'aiSettings' : null)}
        open={openDialog === 'aiSettings'}
      />

      <QuestionSettingsDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        courseSettings={courseQuestionSettings}
        mode="homework"
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'homeworkSettings' : null)
        }
        open={openDialog === 'homeworkSettings'}
      />
      <QuestionSettingsDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        courseSettings={coursePrelectureQuestionSettings}
        mode="preLecture"
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'preLectureSettings' : null)
        }
        open={openDialog === 'preLectureSettings'}
      />
      <AssignPreLectureDialog
        component={component}
        componentId={componentId}
        courseId={courseId}
        onOpenChange={(nextOpen) =>
          setOpenDialog(nextOpen ? 'assignPreLecture' : null)
        }
        open={openDialog === 'assignPreLecture'}
      />
      <StudentViewErrorDialog
        message={openStudentView.error?.message ?? null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            openStudentView.reset();
          }
        }}
        open={openStudentView.isError}
      />
    </>
  );
}
